import React, { useEffect, useState, useContext, createContext } from "react";
import { PhotoManagerContext } from "./index";
import PhotoPopup from "./PhotoPopup";
import { EventType, Album, Photo } from "./types";
import Pagination from "./Pagination";
import VideoThumbnail from "../../SavedPhotos/VideoThumbnail";
import Telemetry from "../../../utils/rudderstackAnalytics";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ShareSocialPopup from "../../ShareSocialPopup";
import ConfirmDeletePhoto from "./ConfirmDeletePhoto";
import BundleMediaSelect from "../../BrowseEvent/components/albums/BundleSelectMedia";
import { CartCheckMedia } from "../styles/CartCheckMedia";
import { handleDownload, hidePhotos } from "../API/api";
import Breadcrumbs from "./Breadcrumbs";
import PhotoUploader from "./PhotoUploader";
import {
  useEventData,
  useAlbumData,
  useModalStates,
  getUrlParams
} from "./BreadcrumbsUtilities";
import SelfiePreferenceModal from "./SelfiePreferenceModal";
import SubscriptionOptionsModal from "./SubscriptionOptionsModal";
import UploadActions from "./UploadActions";
import LegacyUploaderCta from "../components/UploadActions/LegacyUploaderCta";
import PhotoVisibilityIcon from "./PhotoVisibilityIcon";

const uploadIcon = require("../../../../public/images/website/photos/red-upload.svg");
const hiddenIcon = require("../../BrowseEvent/styles/imgHide.svg");
const redactedIcon = require("../styles/images/redactedIcon.svg");
const arrowDownIcon = require("../styles/images/white-down-arrow.svg");
import { AlbumBreadcrumbType } from "../components/types";

type PhotoGridContextType = {
  setShowSelfieSearchPreferencePopup: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setShowSubscriptionPopup: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUploader: React.Dispatch<React.SetStateAction<boolean>>;
  uploaderClosed: boolean;
  selectedAlbumDetails: AlbumBreadcrumbType | null;
  loadingAlbum: boolean;
  shouldSetSelfieSearchPreference: boolean;
  hasToSubscribeToFreeEvent: boolean;
};
export const PhotoGridContext = createContext<PhotoGridContextType | null>(
  null
);

interface PhotoGridProps {
  selectedAlbumPhotos: Array<Photo>;
  selectedEvent: EventType;
  selectedAlbum: Album;
  isMobile: boolean;
  navigateToAlbum: (e: any, selectedEvent: EventType) => void;
  navigateToRoot: () => void;
  fetchAlbumPhotosFunction: Function;
  handlePageChange: (page: number) => void;
  nextPhotoPage: number | null;
  photoCurrentPage: number;
  photosLoading: number | null;
  onDeleteSuccess: (albumId: number) => void;
  showActionTooltip: (id: number, title: string, type: string) => void;
  selectedMediaId: Set<number>;
  togglePhotoSelection: (photoId: number) => void;
  clearSelection: () => void;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({
  selectedAlbumPhotos,
  selectedEvent,
  selectedAlbum,
  isMobile,
  navigateToAlbum,
  navigateToRoot,
  handlePageChange,
  nextPhotoPage,
  photoCurrentPage,
  photosLoading,
  onDeleteSuccess,
  fetchAlbumPhotosFunction,
  showActionTooltip,
  selectedMediaId,
  togglePhotoSelection,
  clearSelection
}) => {
  const context = useContext(PhotoManagerContext);

  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [activeShareModalEventId, setActiveShareModalEventId] = useState<
    number | null
  >(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const [showBulkTagging, setShowBulkTagging] = useState<boolean>(false);
  const [showDeletePhotoModal, setShowDeletePhotoModal] =
    useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [showUploader, setShowUploader] = useState<boolean>(false);
  const [showUploaderOptn, setShowUploaderOptn] = useState<boolean>(false);

  const handlePhotoNavigation = (index: number) => {
    if (index >= selectedAlbumPhotos.length - 5 && nextPhotoPage) {
      fetchAlbumPhotosFunction(selectedAlbum!, nextPhotoPage);
    }
  };
  const handlePhotoClick = (photo: any, index: number) => {
    setCurrentPhotoIndex(index);
    setSelectedPhoto(photo);
    Telemetry.track("content-library:photo-opened");
  };

  const handleClosePopup = () => {
    setSelectedPhoto(null);
  };

  useEffect(() => {
    setShowUploader(false);
  }, [selectedAlbum]);

  const handleDeleteSuccess = () => {
    selectedAlbum && onDeleteSuccess(selectedAlbum?.id);
    setShowDeleteModal(false);
  };

  function selectAllVisible(event: React.ChangeEvent<HTMLInputElement>): void {
    if (event.target.checked) {
      selectedAlbumPhotos.forEach(photo => {
        if (!selectedMediaId?.has(photo.id)) {
          togglePhotoSelection(photo.id);
        }
      });
    } else {
      selectedAlbumPhotos.forEach(photo => {
        if (selectedMediaId?.has(photo.id)) {
          togglePhotoSelection(photo.id);
        }
      });
    }
  }

  const hideOrUnhideText = () => {
    if (selectedMediaId?.size === 0) return "hide";
    const shouldHide = selectedAlbumPhotos.some(
      photo => selectedMediaId?.has(photo.id) && !photo.hidden
    );

    return shouldHide ? "hide" : "unhide";
  };

  function confirmDelete(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    setShowDeletePhotoModal(true);
  }

  const onDeletePhotoSuccess = async () => {
    setShowDeletePhotoModal(false);
    clearSelection();
    // First refresh album details to get the correct photosCount
    if (context?.encodedString && selectedAlbum?.id) {
      await context.refreshAlbumDetails(
        selectedAlbum.id,
        context.encodedString
      );
    }
    // Then refresh photos list (don't update photosCount since we just got it from album details)
    fetchAlbumPhotosFunction(selectedAlbum, photoCurrentPage, false);
  };

  const handlePageChangeWithClear = (page: number) => {
    clearSelection();
    handlePageChange(page);
  };

  const onUploadComplete = () => {
    // @ts-ignore
    toastr.success("Upload successful. Photos are being processed...", null, {
      timeOut: 1000
    });
  };
  const closeUploader = () => {
    setShowUploader(false);
    fetchAlbumPhotosFunction(selectedAlbum, 1);
    fetchAlbumData();
  };

  // states and data related albums page
  const {
    showSelfieSearchPreferencePopup,
    setShowSelfieSearchPreferencePopup,
    showSubscriptionPopup,
    setShowSubscriptionPopup
  } = useModalStates();

  const { albumId, referenceId } = getUrlParams();
  const {
    selectedAlbum: albumDetails,
    loadingAlbum,
    fetchAlbumData
  } = useAlbumData(
    albumId?.toString() || null,
    referenceId?.toString() || null
  );

  const shouldSetSelfieSearchPreference = !albumDetails?.optedForFaceIndexing;
  const isFreeEvent = albumDetails?.free;
  const hasToSubscribeToFreeEvent =
    isFreeEvent && !albumDetails?.canUploadToFreeEvent;
  const reloadAlbumData = () => fetchAlbumData();

  const albumHasPhotos = selectedAlbumPhotos.length > 0;
  const isAlbumEmpty = selectedAlbumPhotos.length <= 0;

  interface PhotoProps {
    id: number;
    hidden: boolean;
    redacted: boolean;
    flaggedForModeration?: boolean;
  }

  interface MediaComponentProps {
    photo: PhotoProps;
    index: number;
    children: React.ReactNode;
    disabled?: boolean;
  }
  const MediaContent = ({ photo, index, children, disabled }: MediaComponentProps) => {
    const isFlagged = photo.flaggedForModeration === true;
    return (
      <div
        key={photo.id}
        className={`ws-grid-item ${
          selectedMediaId?.has(photo.id) ? "selected" : ""
        } ${isFlagged ? "ws-grid-item--flagged" : ""}`}
        onClick={disabled ? undefined : () => handlePhotoClick(photo, index)}
      >
        {
          <div
            className="ws-check-icon-container"
            onClick={e => {
              e.stopPropagation();
              togglePhotoSelection(photo.id);
            }}
          >
            <CartCheckMedia
              color={selectedMediaId?.has(photo.id) ? "#259fa5" : "#5d5d5d70"}
              tickMarkColor={
                selectedMediaId?.has(photo.id) ? "white" : "#cbcbcb"
              }
            />
          </div>
        }
        <PhotoVisibilityIcon
          photo={photo}
          showActionTooltip={showActionTooltip}
        />
        {children}
      </div>
    );
  };

  const SubscriptionContent = () => {
    if (albumDetails?.hasFreeSharingSubscription) {
      if (albumDetails?.canManageFreeSharingSubscription) {
        return (
          <span className="secondary-text">
            Your subscription has been cancelled. To continue uploading photos,
            please reactivate your plan.
          </span>
        );
      } else {
        return (
          <span className="secondary-text">
            This can be set by the owner of the event. Please contact the event
            organizer.
          </span>
        );
      }
    } else {
      if (albumDetails?.canCreateFreeSharingSubscription) {
        return (
          <span className="secondary-text">
            In order to upload photos to free events, you need to subscribe.
          </span>
        );
      } else {
        return (
          <span className="secondary-text">
            This can be set by the owner of the event. Please contact the event
            organizer.
          </span>
        );
      }
    }
  };

  const NoPhotosInfo = () => {
    if (shouldSetSelfieSearchPreference) {
      return (
        <div className="upload-cta-info">
          <div className="primary-text">
            Are you ready to add your first photos?
          </div>
          <span className="secondary-text">
            Set your Selfie Search preference to start uploading.
          </span>
        </div>
      );
    }
    if (hasToSubscribeToFreeEvent) {
      return (
        <div className="upload-cta-info">
          <div className="primary-text">
            Before you upload photos.
            <br />
            There’s an issue with your subscription.
          </div>
          <SubscriptionContent />
        </div>
      );
    }
    return (
      <div className="upload-cta-info">
        Are you ready to add your first photos?
      </div>
    );
  };

  return (
    <PhotoGridContext.Provider
      value={{
        setShowSelfieSearchPreferencePopup: setShowSelfieSearchPreferencePopup,
        setShowUploader: setShowUploader,
        uploaderClosed: !showUploader,
        selectedAlbumDetails: albumDetails,
        loadingAlbum: loadingAlbum,
        setShowSubscriptionPopup: setShowSubscriptionPopup,
        shouldSetSelfieSearchPreference: shouldSetSelfieSearchPreference,
        hasToSubscribeToFreeEvent: hasToSubscribeToFreeEvent || false
      }}
    >
      <div className="geo-photogrid uppy-uploads-enabled">
        <Breadcrumbs viewType="PhotoGrid" />
        <div className="ws-curate-actions-header">
          <div>
            <span className="check-radio checkmark">
              <label htmlFor="select-all-vis" className="geo-tag-b__selectall">
                <input
                  id="select-all-vis"
                  type="checkbox"
                  checked={
                    selectedMediaId?.size === selectedAlbumPhotos.length &&
                    selectedMediaId?.size >= 1
                  }
                  className="geo-form__checkbox"
                  onChange={selectAllVisible}
                />
                <span className="r-dot"></span>
                <span className="r-desc">
                  {selectedAlbumPhotos.length < 50
                    ? "Select all"
                    : `Select all visible (${selectedAlbumPhotos.length})`}
                </span>
              </label>
            </span>
          </div>
          <div className="ws-action-section">
            {selectedEvent.isAuthorizedForDirectDownloads && (
              <button
                className="geo-btn geo-btn--secondary-ghost geo-btn--caps geo-btn--small"
                onClick={() => {
                  setIsDownloading(true);
                  handleDownload(selectedMediaId, selectedEvent.id).finally(
                    () => (setIsDownloading(false), clearSelection())
                  );
                }}
                disabled={selectedMediaId?.size < 1 || isDownloading}
              >
                {isDownloading ? "Preparing..." : "Download"}
              </button>
            )}
            {selectedEvent.canBulkTagPhotos && (
              <button
                className="geo-btn geo-btn--secondary-ghost geo-btn--caps geo-btn--small"
                onClick={() => setShowBulkTagging(true)}
                disabled={selectedMediaId?.size < 1}
              >
                Tag
              </button>
            )}

            {selectedEvent.canBulkHidePhotos && (
              <button
                className="geo-btn geo-btn--secondary-ghost geo-btn--caps geo-btn--small"
                onClick={() =>
                  hidePhotos(
                    selectedMediaId,
                    hideOrUnhideText(),
                    selectedEvent?.id,
                    clearSelection,
                    fetchAlbumPhotosFunction,
                    selectedAlbum,
                    photoCurrentPage
                  )
                }
                disabled={selectedMediaId?.size < 1}
              >
                {hideOrUnhideText()}
              </button>
            )}

            {selectedAlbum.canCurrentUserDelete && (
              <button
                className="geo-btn geo-btn--secondary-ghost geo-btn--caps geo-btn--small"
                onClick={confirmDelete}
                disabled={selectedMediaId?.size < 1}
              >
                Delete
              </button>
            )}
          </div>
        </div>
        {isAlbumEmpty && !photosLoading && (
          <div className="ws-photo-grid">
            <div className="ws-no-photos-wrap">
              <div className="ws-no-photos-div">
                <img src={uploadIcon} alt="" />
                {albumDetails?.canUpload === true ? (
                  <NoPhotosInfo />
                ) : (
                  <div className="upload-cta-info">
                    You don't have access to this album
                  </div>
                )}
              </div>
              {albumDetails?.canUpload === true && (
                <UploadActions
                  selectedEventId={selectedEvent.id}
                  setShowUploaderOption={setShowUploaderOptn}
                  showUploaderOption={showUploaderOptn}
                  selectedAlbum={albumDetails}
                  setShowSelfieSearchPreferencePopup={
                    setShowSelfieSearchPreferencePopup || null
                  }
                  source="photogrid"
                  key="photogrid"
                  setShowSubscriptionPopup={setShowSubscriptionPopup}
                />
              )}
            </div>
          </div>
        )}
        {showUploader && (
          <div className="uploader-in-content-library">
            <PhotoUploader
              albumId={selectedAlbum.id}
              onUploadComplete={onUploadComplete}
              closeUploader={closeUploader}
              hasVideoAccess={selectedAlbum.hasVideoAccess}
              mobileCameraOnlyUpload={selectedAlbum.mobileCameraOnlyUpload}
            />
          </div>
        )}
        <div className="ws-photo-grid">
          {albumHasPhotos
            ? selectedAlbumPhotos.map((photo, index) => (
                <React.Fragment key={photo.id}>
                  {photo.flaggedForModeration ? (
                    <MediaContent photo={photo} index={index} disabled={photo.videoContent && !photo.thumbnail}>
                      <div className="ws-grid-item__flagged-overlay">
                        <svg className="ws-grid-item__flagged-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </MediaContent>
                  ) : photo.videoContent ? (
                    <MediaContent photo={photo} index={index} disabled={!photo.thumbnail}>
                      <VideoThumbnail
                        id={photo.id}
                        thumbnail={photo.thumbnail}
                        videoDuration={photo.videoDuration}
                      />
                    </MediaContent>
                  ) : photo.thumbnail ? (
                    <MediaContent photo={photo} index={index}>
                      <img
                        className="ws-thumb-photo"
                        src={photo.thumbnail}
                        alt={`Photo ${photo.id}`}
                      />
                    </MediaContent>
                  ) : (
                    <div key={photo.id} className="ws-grid-item">
                      <div className="ws-empty-img">
                        <span className="loading-spinner">
                          <span className="fa fa-spinner fa-spin"></span>
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            : null}
        </div>
        {selectedPhoto && (
          <PhotoPopup
            photo={selectedPhoto}
            isMobile={isMobile}
            onClose={handleClosePopup}
            eventName={selectedEvent?.name ?? selectedAlbum?.eventName}
            eventId={selectedEvent?.id ?? selectedAlbum?.eventId}
            selectedAlbum={selectedAlbum}
            fetchAlbumPhotosFunction={fetchAlbumPhotosFunction}
            albumName={selectedAlbum?.name || ""}
            mediaIndex={currentPhotoIndex}
            mediaAlbum={selectedAlbumPhotos}
            onNavigate={(index: number) => {
              setCurrentPhotoIndex(index);
              setSelectedPhoto(selectedAlbumPhotos[index]);
              handlePhotoNavigation(index);
            }}
            photoCurrentPage={photoCurrentPage}
            showActionTooltip={showActionTooltip}
            selectedEvent={selectedEvent}
          />
        )}
        {selectedAlbumPhotos.length > 0 && (
          <Pagination
            currentPage={photoCurrentPage}
            nextPage={nextPhotoPage}
            handlePageChange={handlePageChangeWithClear}
            loading={photosLoading}
          />
        )}
        {showDeleteModal && (
          <ConfirmDeleteModal
            albumId={selectedAlbum?.id || 0}
            onClose={() => setShowDeleteModal(false)}
            onDeleteSuccess={() => handleDeleteSuccess()}
            albumName={selectedAlbum?.name || ""}
          />
        )}
        {activeShareModalEventId && (
          <div className="event-share-modal">
            <ShareSocialPopup
              galleryURL={selectedAlbum?.shareAlbumURL || ""}
              hideShareGalleryModal={() => setActiveShareModalEventId(null)}
              text={"Share with participants or just show off ;)"}
            />
          </div>
        )}

        {showBulkTagging && (
          <BundleMediaSelect
            listOfMediaId={Array.from(selectedMediaId).map(id => ({
              id,
              hidden: false
            }))}
            arrayOfIds={Array.from(selectedMediaId)}
            mediaCount={selectedMediaId?.size}
            contentTagsAPI={"/api/v1/content_tags"}
            handleClose={() => setShowBulkTagging(false)}
            handlePostCall={() => {
              clearSelection();
            }}
          />
        )}

        {showDeletePhotoModal && (
          <ConfirmDeletePhoto
            onClose={() => setShowDeletePhotoModal(false)}
            selectedMediaId={Array.from(selectedMediaId)}
            selectedEventId={selectedEvent?.id || 0}
            onDeletePhotoSuccess={onDeletePhotoSuccess}
            selectedAlbumId={selectedAlbum?.id || 0}
          />
        )}
      </div>
      {showSelfieSearchPreferencePopup && (
        <SelfiePreferenceModal
          onClose={() => setShowSelfieSearchPreferencePopup(false)}
          reloadAlbumData={reloadAlbumData}
          canUpdateSelfiePreference={
            albumDetails?.canUpdateSelfiePreference || false
          }
        />
      )}
      {showSubscriptionPopup && (
        <SubscriptionOptionsModal
          selectedAlbum={albumDetails}
          onClose={() => setShowSubscriptionPopup(false)}
        />
      )}
    </PhotoGridContext.Provider>
  );
};

export default PhotoGrid;
